import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import type { OnChangeFn, VisibilityState } from '@tanstack/react-table';
import { SubmissionStatus, WaitingOn, todayIso } from '@clawhire/shared';
import { useAuth } from '@/auth/AuthProvider';
import { isApiError } from '@/lib/api';
import { enumOptions } from '@/lib/format';
import { PageHeader } from '@/components/ui/page-header';
import { Badge } from '@/components/ui/badge';
import { Board } from '@/components/data/Board';
import { DataTable } from '@/components/data/DataTable';
import { FilterBar, type FilterDef } from '@/components/data/FilterBar';
import { ViewSwitcher } from '@/components/data/ViewSwitcher';
import { useSavedViews } from '@/components/data/useSavedViews';
import { boardGroupsFor } from '@/components/data/tones';
import { useSubmissionList } from '@/pages/submissions/api';
import { SUBMISSION_COLUMN_IDS, submissionColumns } from '@/pages/submissions/columns';
import { SubmissionCard } from '@/pages/submissions/SubmissionCard';
import { useSubmissionStatusChange } from '@/pages/submissions/useStatusChange';
import type { SubmissionListItem } from '@/pages/submissions/types';

const FILTERS: FilterDef[] = [
  { key: 'status', label: 'Status', type: 'enum', options: enumOptions(SubmissionStatus) },
  { key: 'waitingOn', label: 'Waiting on', type: 'enum', options: enumOptions(WaitingOn) },
  { key: 'needsChase', label: 'Needs chase', type: 'boolean' },
  { key: 'chaseList', label: 'Chase list', type: 'boolean' },
  { key: 'position', label: 'Position', type: 'text', placeholder: 'Position (request no. or role)' },
  { key: 'candidate', label: 'Candidate', type: 'text', placeholder: 'Candidate (ID or name)' },
  { key: 'text', label: 'Search', type: 'text', placeholder: 'Search role, candidate, notes…' },
];

const BOARD_GROUPS = boardGroupsFor(SubmissionStatus);
const groupKey = (s: SubmissionListItem) => s.status;
const getItemId = (s: SubmissionListItem) => s.id;

function sameList(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

/**
 * `/submissions` — the candidate × position pipeline with saved views: "All submissions" (table,
 * newest first), "By status" (board, drag & drop changes the status) and "Chase list"
 * (`chaseList=true`, sorted by follow-up date, overdue rows highlighted).
 */
export function SubmissionsListPage() {
  const { canWrite } = useAuth();
  const navigate = useNavigate();
  const sv = useSavedViews('SUBMISSION');
  const list = useSubmissionList(sv.state.filters);
  const items = React.useMemo(() => list.data?.items ?? [], [list.data]);
  const status = useSubmissionStatusChange();

  const today = todayIso();
  const chaseMode = sv.state.filters.chaseList === true;
  const columns = React.useMemo(() => submissionColumns(today), [today]);

  // Saved views carry a `columns` list. The seeded system views predate some columns (next step,
  // chase flag), so their list is only applied once the user touched the column chooser.
  const columnVisibility = React.useMemo<VisibilityState>(() => {
    const cols = sv.state.columns;
    const viewCols = sv.activeView?.columns ?? [];
    const untouchedSystemView = Boolean(sv.activeView?.isSystem) && sameList(cols, viewCols);
    if (cols.length === 0 || untouchedSystemView) return {};
    return Object.fromEntries(SUBMISSION_COLUMN_IDS.map((id) => [id, cols.includes(id)]));
  }, [sv.state.columns, sv.activeView]);
  const onColumnVisibilityChange: OnChangeFn<VisibilityState> = (updater) => {
    const next = typeof updater === 'function' ? updater(columnVisibility) : updater;
    sv.setColumns(SUBMISSION_COLUMN_IDS.filter((id) => next[id] !== false));
  };

  const toChase = chaseMode ? items.filter((s) => s.computed.needsChase).length : 0;
  // A position/candidate reference that matches nothing answers 404 while the user is still typing;
  // that is an empty result, not a failure.
  const noMatch = list.isError && isApiError(list.error) && list.error.status === 404;

  return (
    <>
      <PageHeader
        title="Submissions"
        description={
          chaseMode
            ? `Open submissions sorted by follow-up date · ${toChase} to chase now`
            : 'Candidate × position pipeline: who was proposed where, what we wait for and what to chase.'
        }
        meta={
          list.data ? (
            <Badge variant="secondary" aria-label={`${list.data.total} submissions`}>
              {list.data.total}
            </Badge>
          ) : null
        }
      />

      <div className="space-y-3">
        <ViewSwitcher {...sv} filterDefs={FILTERS} canSave={canWrite} />
        <FilterBar filters={FILTERS} values={sv.state.filters} onChange={sv.setFilters} />

        {list.isError && !noMatch ? (
          <p role="alert" className="text-sm text-destructive">
            Could not load submissions: {list.error.message}
          </p>
        ) : null}

        {sv.state.layout === 'BOARD' ? (
          <Board
            items={items}
            groups={BOARD_GROUPS}
            groupKey={groupKey}
            getItemId={getItemId}
            renderCard={(s) => <SubmissionCard item={s} today={today} />}
            onMove={(id, from, to) => {
              const item = items.find((s) => s.id === id);
              if (!item || !SubmissionStatus.is(from) || !SubmissionStatus.is(to)) return;
              status.request({ id, title: item.computed.title, from, to });
            }}
            onCardClick={(s) => navigate(`/submissions/${s.id}`)}
            readOnly={!canWrite || status.isPending}
            label="Submissions by status"
          />
        ) : (
          <DataTable
            columns={columns}
            data={items}
            isLoading={list.isLoading}
            rowHref={(s) => `/submissions/${s.id}`}
            sorting={sv.state.sorts}
            onSortingChange={(u) => sv.setSorts(typeof u === 'function' ? u(sv.state.sorts) : u)}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            hideSearch
            caption="Submissions"
            emptyTitle={noMatch ? 'No submissions match' : chaseMode ? 'Nothing to chase' : 'No submissions'}
            emptyDescription={
              noMatch
                ? 'No position or candidate matches the filter text.'
                : chaseMode
                  ? 'No open submission has a follow-up or overdue feedback right now.'
                  : 'Propose a candidate for a position from the candidate page.'
            }
          />
        )}
      </div>

      {status.dialog}
    </>
  );
}
